<!--
  이 컴포넌트는 사용자가 회의 코드와 이름을 입력하고,
  마이크/카메라 설정과 함께 회의에 참가할 수 있도록 돕는 모달입니다.
  또한, 사용자는 회의 자료 파일을 업로드할 수 있으며,
  API를 통해 회의 ID를 조회하고, FastAPI 서버에 파일을 업로드합니다.
  이후 새 창으로 회의룸을 열고 참가자가 참여할 수 있도록 처리됩니다.
-->

<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <FileUploadProgressModal v-if="isUploading" :progress="uploadProgress" />
    <div class="modal-content">
      <div class="modal-header">
        <h2 class="text-xl font-bold">강의 참가</h2>
        <button @click="$emit('close')" class="close-button">×</button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-form">
        <!-- 회의 코드 입력 -->
        <div class="form-group">
          <label for="meetingCode">강의 코드</label>
          <input 
            type="text" 
            id="meetingCode" 
            v-model="formData.meetingCode" 
            required
            placeholder="강의 코드 입력"
          >
        </div>

        <!-- 장치 체크 -->
        <div class="device-check">
          <h3 class="text-lg font-semibold mb-3">장치 확인</h3>
          
          <div class="device-options">
            <label class="device-option">
              <input 
                type="checkbox" 
                v-model="formData.useAudio"
              >
              <span>마이크 사용</span>
            </label>

            <label class="device-option">
              <input 
                type="checkbox" 
                v-model="formData.useVideo"
              >
              <span>카메라 사용</span>
            </label>
          </div>

          <div class="preview-container" v-if="formData.useVideo">
            <video ref="videoPreview" class="video-preview" autoplay muted></video>
          </div>
        </div>

        <!-- 파일 첨부 UI -->
        <div class="form-group">
          <label>강의 자료 첨부</label>
          <!-- 📎 지원 파일 형식 안내 텍스트 추가 -->
          <small class="file-format-info">
            <i class="fas fa-info-circle"></i>
            지원 형식: PPT, PPTX, DOC, DOCX, PDF
          </small>
          <div 
            class="drop-zone"
            @dragover.prevent
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
            :class="{ 'disabled': isSubmitting }"
          >
            <p><i class="fas fa-cloud-upload-alt mr-2"></i> 파일을 드래그하거나 클릭하여 업로드하세요</p>
            <!-- 📎 파일 형식 제한: PPT, Word, PDF만 허용 -->
            <input 
              type="file" 
              ref="fileInput"
              @change="handleFileChange" 
              multiple
              accept=".ppt,.pptx,.doc,.docx,.pdf"
              style="display: none"
              :disabled="isSubmitting"
            />
          </div>
          
          <div class="file-list" v-if="formData.files.length > 0">
            <div v-for="(file, index) in formData.files" :key="index" class="file-item">
              <span>{{ file.name }}</span>
              <button type="button" @click="removeFile(index)" class="remove-file-btn">×</button>
            </div>
          </div>
        </div>

        <!-- 제출 버튼 -->
        <div class="form-actions">
          <button type="button" @click="$emit('close')" class="cancel-button">
            취소
          </button>
          <button type="submit" class="submit-button" :disabled="isSubmitting">
            {{ isSubmitting ? '참가 중...' : '강의 참가' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, onUnmounted, watch, defineEmits } from 'vue'
import { restApiClient, apiClientV1 } from '@/api/apiConfig'
import { useAuth } from '@/composables/useAuth'
import FileUploadProgressModal from './FileUploadProgressModal.vue'

const emit = defineEmits(['close', 'join'])
const { user } = useAuth() // 로그인된 사용자 정보 가져오기

const isSubmitting = ref(false)
const videoPreview = ref(null)
let stream = null

// 파일 업로드 진행률 상태 추가
const isUploading = ref(false)
const uploadProgress = ref(0)

// 사용자 이름을 내부적으로 관리할 별도 변수 추가
const userName = ref('')

// 회의 참가를 위한 정보 저장 변수들 추가
const meetingId = ref('')
const joinCode = ref('')

// 사용자 입력 데이터
const formData = reactive({
  meetingCode: '',
  useAudio: true,
  useVideo: true,
  files: []
})

const fileInput = ref(null)

// 현재 로그인한 사용자의 이름을 가져오는 함수 추가
const fetchCurrentUserName = async () => {
  try {
    const response = await restApiClient.get('/restapi/users/name')
    userName.value = response.name
  } catch (error) {
    console.error('[오류] 사용자 이름 조회 중 오류:', error)
    // API 호출 실패 시 세션 스토리지의 정보를 fallback으로 사용
    userName.value = user.value?.name || '알수없음'
  }
}

// 파일 입력 창 트리거
function triggerFileInput() {
  if (!isSubmitting.value) fileInput.value.click()
}

function handleFileChange(event) {
  addFiles(event.target.files)
  event.target.value = ''
}

function handleDrop(event) {
  addFiles(event.dataTransfer.files)
}

onMounted(async () => {
  // 컴포넌트 마운트 시 사용자 이름을 먼저 가져와서 userName 변수에 자동 설정
  await fetchCurrentUserName()
  
  if (formData.useVideo) startVideoPreview()
})

// 📎 파일 추가 메서드 - PPT, Word, PDF 파일만 허용하도록 검증 로직 추가
function addFiles(newFiles) {
  // 허용되는 파일 확장자 목록
  const allowedExtensions = ['.ppt', '.pptx', '.doc', '.docx', '.pdf'];
  
  // 허용되는 MIME 타입 목록 (보안 강화를 위한 이중 검증)
  const allowedMimeTypes = [
    'application/vnd.ms-powerpoint',                                             // .ppt
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
    'application/msword',                                                        // .doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',   // .docx
    'application/pdf'                                                            // .pdf
  ];
  
  for (let file of newFiles) {
    // 파일 확장자 검사
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
    
    // MIME 타입 검사 (추가 보안)
    const hasValidMimeType = allowedMimeTypes.includes(file.type);
    
    // 두 조건을 모두 만족하는 경우에만 파일 추가
    if (hasValidExtension && hasValidMimeType) {
      formData.files.push(file);
    } else {
      // ❌ 지원하지 않는 파일 형식일 경우 사용자에게 알림
      alert(`지원하지 않는 파일 형식입니다: ${file.name}\n지원 형식: PPT, PPTX, DOC, DOCX, PDF`);
    }
  }
}

function removeFile(index) {
  formData.files.splice(index, 1)
}

// 비디오 프리뷰 시작
async function startVideoPreview() {
  if (formData.useVideo && videoPreview.value) {
    try {
      stream = await navigator.mediaDevices.getUserMedia({ video: true })
      videoPreview.value.srcObject = stream
    } catch (error) {
      console.error('[오류] 카메라 접근 실패:', error)
      formData.useVideo = false
    }
  }
}

// 비디오 프리뷰 종료
function stopVideoPreview() {
  if (stream) {
    stream.getTracks().forEach(track => track.stop())
    stream = null
  }
  if (videoPreview.value) videoPreview.value.srcObject = null
}

// 비디오 설정 변경 시 자동 반영
watch(() => formData.useVideo, (use) => {
  use ? startVideoPreview() : stopVideoPreview()
})

onMounted(() => {
  if (formData.useVideo) startVideoPreview()
})

onUnmounted(() => {
  stopVideoPreview()
})

// 회의방으로 이동하는 함수
function goToMeeting() {

  try {
    const currentMeetingId = meetingId.value;
    const currentJoinCode = joinCode.value;
    
    if (!currentMeetingId || !currentJoinCode) {
      throw new Error('강의 정보가 없습니다.');
    }

    // 1. 보안 토큰 생성
    const securityToken = {
      accessKey: crypto.randomUUID(),
      meetingId: currentMeetingId,
      role: 'participant',
      userId: user.value?.id || 'guest',
      timestamp: Date.now(),
      expiresAt: Date.now() + (24 * 60 * 60 * 1000),
      checksum: btoa(`${currentMeetingId}_${Date.now()}_${crypto.randomUUID()}`)
    };

    // 2. 세션스토리지에 검증용 토큰 저장 (MeetingRoom에서 비교할 용도)
    try {
      sessionStorage.setItem('meetingSecurityToken', JSON.stringify(securityToken));
      sessionStorage.setItem('currentMeetingCode', currentJoinCode);
      sessionStorage.setItem('currentMeetingId', currentMeetingId);
      console.log('검증용 토큰이 세션스토리지에 저장됨');
    } catch (storageError) {
      console.error('세션스토리지 저장 실패:', storageError);
      throw new Error('브라우저 저장소에 접근할 수 없습니다.');
    }

    // 3. URL에 토큰 포함해서 생성
    const urlParams = new URLSearchParams({
      key: securityToken.accessKey,
      token: btoa(JSON.stringify(securityToken)),
      code: btoa(currentJoinCode),
      userName: btoa(encodeURIComponent(userName.value)) // URL로만 전달
    });
    
    const meetingUrl = `/meeting/${currentMeetingId}?${urlParams.toString()}`;
    
    // 4. 비디오 프리뷰 정지 및 새 탭에서 회의방 열기
    stopVideoPreview();
    
    const newWindow = window.open(meetingUrl, '_blank');
    
    if (!newWindow) {
      throw new Error('팝업이 차단되었습니다. 브라우저 설정을 확인해주세요.');
    }
    
    // 5. 새 탭이 완전히 로드된 후 기존 탭의 세션 정리
    setTimeout(() => {
      try {
        sessionStorage.removeItem('meetingSecurityToken');
        sessionStorage.removeItem('currentMeetingCode');
        sessionStorage.removeItem('currentMeetingId');
        console.log('기존 탭의 세션 스토리지 정리 완료');
      } catch (cleanupError) {
        console.warn('세션 스토리지 정리 중 오류:', cleanupError);
      }
    }, 2000); // 2초 후 정리
    
    emit('close');
    
  } catch (error) {
    console.error('강의 이동 중 오류 발생:', error);
    alert(error.message || '강의로 이동할 수 없습니다. 다시 시도해주세요.');
  }
}

// 회의 참가 핸들러 - 검증 및 준비 작업만 수행
async function handleSubmit() {
  // 회의 코드만 검증 (이름은 자동으로 설정되므로 검증 불필요)
  if (!formData.meetingCode.trim()) {
    alert('강의 코드를 입력해주세요.');
    return
  }

  // userName이 설정되지 않은 경우 추가 검증
  if (!userName.value.trim()) {
    alert('사용자 정보를 불러오는 중입니다. 잠시 후 다시 시도해주세요.');
    return
  }

  isSubmitting.value = true;
  const inputCode = formData.meetingCode.trim();

  try {
    // 1. 회의 ID 조회 (백엔드에서 title도 함께 반환한다고 가정)
    const response = await restApiClient.get(`/restapi/meetings/id?code=${inputCode}`);

    // ref 변수에 값 저장
    meetingId.value = response.meetingId;
    const meetingTitle = response.title; // API 응답에서 회의명 추출
    joinCode.value = inputCode; // ← 사용자가 입력한 회의 코드로 설정
    console.log('강의명:', meetingTitle);
    console.log('강의 ID:', meetingId.value);
    console.log('강의 코드:', joinCode.value);

    sessionStorage.setItem('currentMeetingCode', joinCode.value);
    sessionStorage.setItem('currentMeetingId', meetingId.value);

    // 3. 파일 업로드 처리
    const fileFormData = new FormData()
    formData.files.forEach(file => fileFormData.append('files', file))

    const userStr = sessionStorage.getItem('user')
    let sessionUserId = null
    let userLanguage = null
    try {
      if (userStr) {
        const user = JSON.parse(userStr)
        sessionUserId = user.id
        userLanguage = user.language
      }
    } catch (e) {
      console.error('[오류] 세션 사용자 정보 파싱 실패:', e)
    }

    fileFormData.append('meetingId', meetingId.value)
    if (sessionUserId) fileFormData.append('userId', sessionUserId)
    if (userLanguage) fileFormData.append('language', userLanguage)
    fileFormData.append('code', joinCode.value)
    
    if (meetingTitle) {
      fileFormData.append('meeting_name', meetingTitle);
    }

    // 파일이 있을 때만 프로그레스 모달 표시
    if (formData.files.length > 0) {
      isUploading.value = true;
      uploadProgress.value = 0;
    }
    
    const totalSize = formData.files.reduce((acc, file) => acc + file.size, 0);
    const totalMb = totalSize / (1024 * 1024);
    const estimatedDurationMs = 2000 + totalMb * 2000;
    
    let progressInterval = null;

    try {
      if (formData.files.length > 0) {
        const startTime = Date.now();
        progressInterval = setInterval(() => {
          const elapsedTime = Date.now() - startTime;
          const progress = (elapsedTime / estimatedDurationMs) * 100;
          uploadProgress.value = Math.min(99, Math.floor(progress));
        }, 150);
      }

      await apiClientV1.post('/api/v1/uploads', fileFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (formData.files.length > 0) {
        uploadProgress.value = 100;
      }

    } catch (uploadError) {
      console.error('[오류] 업로드 실패:', uploadError);
      // 여기서도 오류가 발생해도 회의 참가는 계속 진행되도록 합니다.
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      if (formData.files.length > 0) {
        await new Promise(resolve => setTimeout(resolve, 500));
        isUploading.value = false;
      }
    }

    // 회의 방으로 이동
    goToMeeting();

  } catch (error) {
    console.error('[오류] 강의 참가 실패:', error)
    alert('강의 참가 실패: ' + (error.message || '알 수 없는 오류'))
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 1rem;
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  scrollbar-width: none;        /* Firefox */
  -ms-overflow-style: none;     /* IE, Edge */
}
.modal-content::-webkit-scrollbar {
  display: none;                /* Chrome, Safari, Opera */
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.close-button {
  font-size: 1.5rem;
  line-height: 1;
  padding: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 500;
}

.form-group input {
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
}

.device-check {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 1rem;
}

.device-options {
  display: flex;
  gap: 2rem;
  margin-bottom: 1rem;
}

.device-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.preview-container {
  width: 100%;
  aspect-ratio: 16/9;
  background: #f1f5f9;
  border-radius: 0.5rem;
  overflow: hidden;
}

.video-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 1rem;
}

.cancel-button {
  padding: 0.5rem 1rem;
  background: #f1f5f9;
  border-radius: 0.5rem;
  color: var(--primary);
  border: 1px solid var(--primary);
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-button:hover {
  background: rgba(0, 51, 160, 0.05);
}

.submit-button {
  padding: 0.5rem 1rem;
  background: var(--primary);
  color: white;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 51, 160, 0.2);
}

.submit-button:hover:not(:disabled) {
  background: var(--primary-hover);
  box-shadow: 0 4px 12px rgba(0, 51, 160, 0.3);
  transform: translateY(-1px);
}

.submit-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 파일 업로드 관련 스타일 */
.drop-zone {
  border: 2px dashed #e2e8f0;
  border-radius: 0.5rem;
  padding: 1.5rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.5rem;
}

.drop-zone:hover:not(.disabled) {
  border-color: #4f46e5;
  background-color: #f8fafc;
}

.drop-zone.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.file-list {
  margin-top: 1rem;
}

.file-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-color: #f1f5f9;
  border-radius: 0.25rem;
  margin-bottom: 0.5rem;
}

.remove-file-btn {
  background: none;
  border: none;
  color: #ef4444;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0 0.25rem;
}

.remove-file-btn:hover {
  color: #dc2626;
}

/* 파일 형식 안내 텍스트 스타일 */
.file-format-info {
  color: #64748b;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  font-weight: 500;
}

.file-format-info i {
  color: #3b82f6;
  font-size: 0.75rem;
}
</style>